// Google Analytics 4 Service
// Measurement ID: G-19S11DT858

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

const GA_MEASUREMENT_ID = 'G-19S11DT858';
const AFFILIATE_REF_KEY = 'ga_affiliate_ref';

/** Capture ?ref= from URL on first load for affiliate attribution on deposit */
function captureAffiliateRef(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref') || params.get('utm_source') || params.get('affiliate');
    if (ref) sessionStorage.setItem(AFFILIATE_REF_KEY, ref);
  } catch {
    // ignore
  }
}

// Run once on load
if (typeof window !== 'undefined') captureAffiliateRef();

class GoogleAnalyticsService {
  private isEnabled: boolean = true;

  constructor() {
    if (typeof window !== 'undefined' && !window.gtag) {
      console.warn('Google Analytics not loaded');
    }
  }

  /** Get stored affiliate/ref (e.g. from ?ref=affiliate123). Use when tracking deposit. */
  getAffiliateRef(): string | null {
    if (typeof window === 'undefined') return null;
    try {
      return sessionStorage.getItem(AFFILIATE_REF_KEY);
    } catch {
      return null;
    }
  }

  /** Call on app init to capture ref from current URL (e.g. after redirect). */
  captureAffiliateRefFromUrl(): void {
    captureAffiliateRef();
  }

  // Track page view (SPA route changes + initial load via index.html)
  trackPageView(path: string, title?: string) {
    if (!this.isEnabled || typeof window === 'undefined' || !window.gtag) return;

    const pageLocation = typeof window.location !== 'undefined' ? window.location.origin + path : undefined;
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: path,
      page_title: title || document.title,
      ...(pageLocation && { page_location: pageLocation }),
    });
  }

  // Track custom event
  trackEvent(eventName: string, params?: Record<string, any>) {
    if (!this.isEnabled || typeof window === 'undefined' || !window.gtag) return;

    window.gtag('event', eventName, params);

    console.log('📊 GA Event:', eventName, params);
  }

  // Track user login
  trackLogin(method: string) {
    this.trackEvent('login', { method });
  }

  // Track user signup (method: 'email' | 'google' | 'microsoft' | 'telegram')
  trackSignup(method: string) {
    this.trackEvent('sign_up', { method });
  }

  // Track button click
  trackButtonClick(buttonName: string, category?: string) {
    this.trackEvent('click', {
      event_category: category || 'button',
      event_label: buttonName,
    });
  }

  // Track game launch
  trackGameLaunch(gameName: string, gameType: string, provider?: string) {
    this.trackEvent('game_launch', {
      game_name: gameName,
      game_type: gameType,
      provider: provider || 'unknown',
    });
  }

  // Track deposit (value, currency; optional affiliate for ?ref= attribution)
  trackDeposit(amount: number, currency: string, method?: string, affiliate?: string | null) {
    const params: Record<string, unknown> = { value: amount, currency };
    if (method) params.payment_method = method;
    if (affiliate) params.affiliate = affiliate;
    this.trackEvent('deposit', params);
  }

  // Track withdrawal (event name 'withdraw' for funnel reports)
  trackWithdrawal(amount: number, currency: string, method?: string) {
    const params: Record<string, unknown> = { value: amount, currency };
    if (method) params.payment_method = method;
    this.trackEvent('withdraw', params);
  }

  // Track bet placed (event 'place_bet' for funnel: value, game)
  trackBetPlaced(amount: number, gameType: string, odds?: number) {
    this.trackEvent('place_bet', {
      value: amount,
      game: gameType,
      ...(odds != null && { odds }),
    });
  }

  // Track search
  trackSearch(searchTerm: string) {
    this.trackEvent('search', {
      search_term: searchTerm,
    });
  }

  // Track error
  trackError(errorMessage: string, errorType?: string) {
    this.trackEvent('exception', {
      description: errorMessage,
      fatal: false,
      error_type: errorType,
    });
  }

  // Set user ID (for logged in users)
  setUserId(userId: string) {
    if (!this.isEnabled || typeof window === 'undefined' || !window.gtag) return;

    window.gtag('config', GA_MEASUREMENT_ID, {
      user_id: userId,
    });
  }

  // Set user properties
  setUserProperties(properties: Record<string, any>) {
    if (!this.isEnabled || typeof window === 'undefined' || !window.gtag) return;

    window.gtag('set', 'user_properties', properties);
  }

  // Enable/disable tracking
  setEnabled(enabled: boolean) {
    this.isEnabled = enabled;
    console.log('📊 GA', enabled ? 'enabled' : 'disabled');
  }
}

export const googleAnalytics = new GoogleAnalyticsService();
export default googleAnalytics;

